import React, { useState, useEffect } from 'react';
import Card from 'react-bootstrap/Card';
import { Button } from 'react-bootstrap';
import 'bootstrap/dist/css/bootstrap.min.css';
import '@trendmicro/react-sidenav/dist/react-sidenav.css';
import "../styles/sidebar.css";
import Card1 from '../images/Card1.png';
import Card2 from '../images/Card2.png';
import { webURL, card1Text, card2Text } from '../config';

const SideBarSmallScreen = () => {
    // State for card rotation
    const [currentCardIndex, setCurrentCardIndex] = useState(0);
    const [transitionPhase, setTransitionPhase] = useState('stable'); // 'stable', 'exiting', 'entering'
    
    // Card data array for easier management
    const cardData = [
        {
            image: Card1,
            text: card1Text,
            id: 'card1'
        },
        {
            image: Card2,
            text: card2Text,
            id: 'card2'
        },
      
    ];

    // Card rotation effect with smooth two-phase transition
    useEffect(() => {
        const cardRotationInterval = setInterval(() => {
            // Phase 1: Exit transition (fade out current card)
            setTransitionPhase('exiting');
            
            setTimeout(() => {
                // Phase 2: Change card and start entrance transition
                setCurrentCardIndex((prevIndex) => (prevIndex + 1) % cardData.length);
                setTransitionPhase('entering');
                
                setTimeout(() => {
                    // Phase 3: Transition complete, card is stable
                    setTransitionPhase('stable');
                }, 600); // 600ms for entrance transition
                
            }, 600); // 600ms for exit transition
            
        }, 20000); // 20 seconds

        return () => clearInterval(cardRotationInterval);
    }, [cardData.length]);

    // Get current card to display
    const currentCard = cardData[currentCardIndex];

    // Dynamic styles based on transition phase
    const getCardStyle = () => {
        const baseStyle = {
            transition: 'all 0.6s ease-in-out',
        };

        switch (transitionPhase) {
            case 'exiting':
                return {
                    ...baseStyle,
                    opacity: 0.2,
                    filter: 'blur(3px)',
                    transform: 'scale(0.95) translateY(10px)',
                };
            case 'entering':
                return {
                    ...baseStyle,
                    opacity: 0.4,
                    filter: 'blur(2px) brightness(0.8)',
                    transform: 'scale(1.05) translateY(-5px)',
                };
            case 'stable':
            default:
                return {
                    ...baseStyle,
                    opacity: 1,
                    filter: 'blur(0px) brightness(1)',
                    transform: 'scale(1) translateY(0px)',
                };
        }
    };

    const getImageStyle = () => {
        const baseStyle = {
            transition: 'all 0.6s ease-in-out',
        };

        switch (transitionPhase) {
            case 'exiting':
                return {
                    ...baseStyle,
                    filter: 'brightness(0.5) blur(2px) saturate(0.7)',
                    transform: 'scale(0.98)',
                    opacity: 0.3,
                };
            case 'entering':
                return {
                    ...baseStyle,
                    filter: 'brightness(1.1) blur(1px) saturate(1.2)',
                    transform: 'scale(1.02)',
                    opacity: 0.7,
                };
            case 'stable':
            default:
                return {
                    ...baseStyle,
                    filter: 'brightness(1) blur(0px) saturate(1)',
                    transform: 'scale(1)',
                    opacity: 1,
                };
        }
    };

    const getTextStyle = () => {
        const baseStyle = {
            transition: 'all 0.6s ease-in-out',
        };

        switch (transitionPhase) {
            case 'exiting':
                return {
                    ...baseStyle,
                    opacity: 0.2,
                    transform: 'translateY(8px) scale(0.98)',
                    filter: 'blur(1px)',
                };
            case 'entering':
                return {
                    ...baseStyle,
                    opacity: 0.6,
                    transform: 'translateY(-3px) scale(1.01)',
                    filter: 'blur(0.5px)',
                };
            case 'stable':
            default:
                return {
                    ...baseStyle,
                    opacity: 1,
                    transform: 'translateY(0px) scale(1)',
                    filter: 'blur(0px)',
                };
        }
    };

    const getButtonStyle = () => {
        const baseStyle = {
            transition: 'all 0.6s ease-in-out',
            backgroundColor: 'transparent',
            margin: '0',
            padding: '0',
        };

        switch (transitionPhase) {
            case 'exiting':
                return {
                    ...baseStyle,
                    opacity: 0.3,
                    filter: 'blur(2px)',
                    transform: 'scale(0.95)',
                };
            case 'entering':
                return {
                    ...baseStyle,
                    opacity: 0.7,
                    filter: 'blur(1px)',
                    transform: 'scale(1.02)',
                };
            case 'stable':
            default:
                return {
                    ...baseStyle,
                    opacity: 1,
                    filter: 'blur(0px)',
                    transform: 'scale(1)',
                };
        }
    };

    // Fixed container styles to prevent layout shifts
    const containerStyle = {
        width: '100%',
        minHeight: '320px', // Adjust based on your design needs
        maxHeight: '320px',
        position: 'relative',
        overflow: 'hidden',
    };

    const cardWrapperStyle = {
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
    };

    const imageContainerStyle = {
        width: '100%',
        height: '180px', // Fixed height for images
        overflow: 'hidden',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#f8f9fa', // Light background for consistency
    };

    const textContainerStyle = {
        flex: '1',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        padding: '12px',
        minHeight: '120px', // Fixed minimum height for text area
        maxHeight: '120px',
        overflow: 'hidden',
    };

    const buttonContainerStyle = {
        marginTop: 'auto',
        paddingTop: '8px',
    };

    return (
        <div className='sidebar-smallscreen-container' style={containerStyle}>
            <Card 
                className='sidebar-smallscreen-card' 
                key={currentCard.id}
                style={{
                    ...getCardStyle(),
                    ...cardWrapperStyle,
                    border: 'none', // Remove default border to prevent size changes
                }}
            >            
                <Card.Body style={{ 
                    padding: 0, 
                    height: '100%', 
                    display: 'flex', 
                    flexDirection: 'column' 
                }}>               
                    <div style={imageContainerStyle}>
                        <Card.Img 
                            variant="top" 
                            src={currentCard.image} 
                            style={{
                                ...getImageStyle(),
                                width: '100%',
                                height: '100%',
                                objectFit: 'cover', // Maintain aspect ratio while filling container
                                objectPosition: 'center',
                            }}
                        />
                    </div>
                    
                    <div style={textContainerStyle}>
                        <Card.Text 
                            className='card-text'
                            style={{
                                ...getTextStyle(),
                                margin: 0,                                
                                lineHeight: '1.4',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                display: '-webkit-box',
                                WebkitLineClamp: 4, // Limit to 4 lines
                                WebkitBoxOrient: 'vertical',
                            }}
                        >
                            {currentCard.text}
                        </Card.Text>
                        
                        <div style={buttonContainerStyle}>
                            <a 
                                href={webURL} 
                                target='_blank' 
                                rel='noreferrer' 
                                style={getButtonStyle()}
                            >
                                <Button 
                                    variant="text" 
                                    className='card-btn'
                                    style={{
                                        width: '100%',
                                        padding: '8px 16px',                                        
                                    }}
                                >
                                    Learn More
                                </Button>
                            </a>
                        </div>
                    </div>              
                </Card.Body>
            </Card>
        </div>
    );
};

export default SideBarSmallScreen;